import React, { useContext, useEffect, useRef, useState } from "react";
import { TextField, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { CommonContext } from "../../../../context/CommonContext";
import { AuthContext } from "../../../../context/AuthContext";
import { chemoAdministrationInitialValues, mapChemoAdministrationResponse } from "../../../../schema/chemoAdministrationInitialValues";
import { authHttp } from "../../../../services/commonHttpService";
import { apisPath, paths } from "../../../../utils/path";
import { FieldArray, Formik } from "formik";
import { chemoAdministrationSchema } from "../../../../schema/chemoAdministrationSchema";
import { useNavigate } from "react-router-dom";
import MedicineSelect from "../../../MedicineSelect/MedicineSelect";
import { formatDate } from "../../../../helpers/commonHelper";

const ChemoAdministration = ({
    patientId,
    submitForm,
    formResult,
    currentStep,
    isRedirected,
    planned_chemo_cycles,
    chemoSessionDate,
    step5Data
}) => {
    const navigate = useNavigate();
    const { setIsLoading, setAlertState } = useContext(CommonContext);
    const { userId } = useContext(AuthContext);

    const [formValues, setFormValues] = useState(chemoAdministrationInitialValues);
    const [isFocus, setIsFocus] = useState(false);
    const [patientRecord, setPatientRecord] = useState(0);
    const formRef = useRef(null);

    // Trigger submit from parent
    useEffect(() => {
        if (submitForm) {
            formRef.current?.handleSubmit();

            if (Object.keys(formRef.current?.errors || {}).length > 0) {
                setIsFocus(true);
            }

            formResult(false); // parent resets submitForm
        }
    }, [submitForm]);

    // Focus & alert on first error
    useEffect(() => {
        if (isFocus && Object.keys(formRef.current?.errors || {}).length) {
            const keys = Object.keys(formRef.current.errors);
            const firstErrorField = keys[0];

            // Try to focus the field
            const element = document.getElementsByName(firstErrorField)[0];
            if (element) {
                element.focus();
            }

            setAlertState({
                open: true,
                title: Object.values(formRef.current.errors)[0],
                btnTxt: "OK",
                status: "fail",
            });

            setIsFocus(false);
        }
    }, [isFocus]);

    // Fetch saved chemo administration data
    useEffect(() => {
        if (currentStep === 1 && patientId) {
            fetchStepData();
        }
    }, [currentStep, patientId, step5Data]);

    const fetchStepData = async () => {
        setIsLoading(true);
        try {
            const response = await authHttp.get(
                `${apisPath?.getMoPatientTreatmentStep}/${patientId}/2`
            );

            if (response?.data?.data) {
                const mappedValues = mapChemoAdministrationResponse(response?.data?.data, step5Data);

                // Store patient_record for cycle calculation
                const record = response?.data?.data?.patient_record || 0;
                setPatientRecord(record);

                // If cycleNumber exists in response, use it; otherwise use patientRecord + 1
                if (!mappedValues.cycleNumber) {
                    mappedValues.cycleNumber = record + 1;
                }

                setFormValues(mappedValues);
            }
        } catch (error) {
            // If 404 → no previous data (expected)
            if (error?.response?.status === 404) {
                console.log("No previous chemo administration data - starting fresh.");
                setPatientRecord(0);

                // Set cycleNumber to patientRecord + 1 (which is 0 + 1 = 1 for new records)
                // Set cycleNumber and merge step5Data for initial values
                const initialValues = mapChemoAdministrationResponse(null, step5Data);
                setFormValues({
                    ...initialValues,
                    cycleNumber: 1
                });
            } else {
                // Real error
                setAlertState({
                    open: true,
                    title: error?.response?.data?.message || "Something went wrong",
                    btnTxt: "CLOSE",
                    status: "fail",
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // FORM SUBMIT HANDLER
    const submitHandler = async (values) => {
        setIsLoading(true);

        try {
            const payload = {
                patient_id: Number(patientId),
                user_id: Number(userId),
                referral_date: values?.referralDate,
                cycle_number: Number(values?.cycleNumber),
                chemo_session_date: chemoSessionDate,

                // dccc_id: values?.dcccId ? Number(values.dcccId) : null,
                // dccc_name: values?.dcccName.trim(),
                // chemo_drug_name: values?.chemoDrugName.trim(),
                // chemo_dose_administered: values?.chemoDoseAdministered ? Number(values.chemoDoseAdministered) : null,
                // chemo_administration_route: values?.chemoAdministrationRoute.trim(),

                chemo_infusion_rate_duration: values?.chemoInfusionRateDuration ? Number(values.chemoInfusionRateDuration) : null,
                chemo_diluent_volume: values?.chemoDiluentVolume ? Number(values.chemoDiluentVolume) : null,
                chemo_timing: values?.chemoTiming ? Number(values.chemoTiming) : null,
                chemo_delivery_method: values?.chemoDeliveryMethod.trim(),
                chemo_notes_special_instructions: values?.chemoNotesSpecialInstructions.trim(),
                pre_chemo_hydration_fluid_type: values?.preChemoHydrationFluidType.trim(),
                pre_chemo_hydration_fluid_volume: values?.preChemoHydrationFluidVolume ? Number(values.preChemoHydrationFluidVolume) : null,
                pre_chemo_hydration_fluid_duration: values?.preChemoHydrationFluidDuration ? Number(values.preChemoHydrationFluidDuration) : null,
                post_chemo_hydration_fluid_type: values?.postChemoHydrationFluidType.trim(),
                post_chemo_hydration_fluid_volume: values?.postChemoHydrationFluidVolume ? Number(values.postChemoHydrationFluidVolume) : null,
                post_chemo_hydration_fluid_duration: values?.postChemoHydrationFluidDuration ? Number(values.postChemoHydrationFluidDuration) : null,

                // chemo_dose_modification_instructions: values?.chemoDoseModificationInstructions.trim(),

                significant_side_effects: values?.significantSideEffects.trim(),
                medicine_prescribed: values?.medicinePrescribed.trim(),
                discharge_summary: values?.dischargeSummary.trim(),
                doctor_name: values?.doctorName.trim(),
                // mobile_number: values?.mobileNumber.trim(),
                drug_details: values?.drugDetails.map(drug => ({
                    drug_name: drug.drugName.trim(),
                    dose_administered: drug.doseAdministered.trim(),
                    administration_route: drug.administrationRoute.trim(),
                })),
                premedication_details: values?.premedicationDetails.map(premed => ({
                    drug_name: premed.drugName.trim(),
                    dose_administered: premed.doseAdministered.trim(),
                    administration_route: premed.administrationRoute.trim(),
                })),
            };

            // Only include session_number if user was redirected (not navigated via Previous button)
            if (isRedirected) {
                payload.session_number = patientRecord + 1;
            }

            await authHttp.post(`${apisPath?.moPatientTreatmentStep}/2`, payload);

            setAlertState({
                open: true,
                title: "Chemotherapy administration details saved successfully!",
                btnTxt: "CLOSE",
                status: "success",
            });
            navigate(`${paths?.user?.commonPoint}${paths?.user?.moBeneficiaryManagement}`);

            formResult(true); // tell parent step successful

        } catch (err) {
            setAlertState({
                open: true,
                title: err?.response?.data?.message || "Failed to save chemo administration data",
                btnTxt: "CLOSE",
                status: "fail",
            });

            formResult(false);
        } finally {
            setIsLoading(false);
        }
    };

    // Determine if cycleNumber field should be disabled
    const isCycleNumberDisabled = !!planned_chemo_cycles;
    // Generate placeholder text
    const cyclePlaceholder = planned_chemo_cycles
        ? `(${patientRecord + 1}/${planned_chemo_cycles})`
        : '';

    return (
        <Formik
            initialValues={formValues}
            enableReinitialize
            validationSchema={chemoAdministrationSchema}
            onSubmit={submitHandler}
            innerRef={formRef}
        >
            {({ values, errors, touched, handleChange, handleBlur }) => (
                <div className="container py-4">
                    <div className="">
                        <h6>Chemotherapy Administration data submission for {formatDate(chemoSessionDate)} session</h6>
                        <hr />
                    </div>
                    <div className="p-4">
                        <h5 className="mb-4 fw-bold">Chemotherapy Administration Details for </h5>

                        {/* Row 1 */}
                        <div className="row">
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Date of administration"
                                    fullWidth
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    name="referralDate"
                                    value={values?.referralDate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Cycle Number"
                                    fullWidth
                                    name="cycleNumber"
                                    value={values?.cycleNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder={cyclePlaceholder}
                                    disabled={isCycleNumberDisabled}
                                    InputProps={{
                                        style: isCycleNumberDisabled ? {
                                            backgroundColor: '#f5f5f5',
                                            cursor: 'not-allowed'
                                        } : {}
                                    }}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Chemotherapy Drugs and Dosage administered"
                                    fullWidth
                                    name="chemoDrugName"
                                    value={values?.chemoDrugName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>

                        {/* Dynamic Drug Rows */}
                        <FieldArray name="drugDetails">
                            {({ push, remove }) => (
                                <>
                                    {values?.drugDetails?.map((drug, index) => (
                                        <div key={index} className="row align-items-center">
                                            <div className="col-md-4 col-12 mb-3">
                                                <MedicineSelect
                                                    name={`drugDetails.${index}.drugName`}
                                                    value={drug?.drugName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    label="Drug Name"
                                                />
                                            </div>
                                            <div className="col-md-4 col-12 mb-3">
                                                <TextField
                                                    label="Dose administered"
                                                    fullWidth
                                                    name={`drugDetails.${index}.doseAdministered`}
                                                    value={drug?.doseAdministered}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </div>
                                            <div className="col-md-3 col-10 mb-3">
                                                <TextField
                                                    label="Route of Administration"
                                                    fullWidth
                                                    name={`drugDetails.${index}.administrationRoute`}
                                                    value={drug?.administrationRoute}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </div>

                                            <div className="col-md-1 col-2 mb-3 d-flex">
                                                {/* Show delete button only if not first row */}
                                                {index > 0 && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => remove(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}

                                                {/* Show add button only on last row */}
                                                {index === values.drugDetails.length - 1 && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            push({ drugName: "", doseAdministered: "", administrationRoute: "" })
                                                        }
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </div>

                                    ))}
                                </>
                            )}
                        </FieldArray>

                        {/* Admin Details */}
                        <div className="row mt-3">
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Infusion Rate/Duration"
                                    fullWidth
                                    name="chemoInfusionRateDuration"
                                    value={values?.chemoInfusionRateDuration}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Diluent/Volume"
                                    fullWidth
                                    name="chemoDiluentVolume"
                                    value={values?.chemoDiluentVolume}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Timing"
                                    fullWidth
                                    name="chemoTiming"
                                    value={values?.chemoTiming}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Delivery Method"
                                    fullWidth
                                    name="chemoDeliveryMethod"
                                    value={values?.chemoDeliveryMethod}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-12 col-12 mb-3">
                                <TextField
                                    label="Notes/Special Instructions"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    name="chemoNotesSpecialInstructions"
                                    value={values?.chemoNotesSpecialInstructions}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>

                        {/* Premedication Section */}
                        <h6 className="fw-bold mt-4">Premedication's Given</h6>

                        <FieldArray name="premedicationDetails">
                            {({ push, remove }) => (
                                <>
                                    {values?.premedicationDetails?.map((premed, index) => (
                                        <div className="row align-items-center" key={index}>
                                            <div className="col-md-4 col-12 mb-3">
                                                <MedicineSelect
                                                    name={`premedicationDetails.${index}.drugName`}
                                                    value={premed?.drugName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    label="Drug Name"
                                                />
                                            </div>

                                            <div className="col-md-4 col-12 mb-3">
                                                <TextField
                                                    label="Dose administered"
                                                    fullWidth
                                                    name={`premedicationDetails.${index}.doseAdministered`}
                                                    value={premed?.doseAdministered}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </div>

                                            <div className="col-md-3 col-10 mb-3">
                                                <TextField
                                                    label="Route of Administration"
                                                    fullWidth
                                                    name={`premedicationDetails.${index}.administrationRoute`}
                                                    value={premed?.administrationRoute}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </div>

                                            <div className="col-md-1 col-2 mb-3 d-flex">
                                                {/* Show delete button only if not first row */}
                                                {index > 0 && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => remove(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}

                                                {/* Show add button only on last row */}
                                                {index === values.premedicationDetails.length - 1 && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            push({ drugName: "", doseAdministered: "", administrationRoute: "" })
                                                        }
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </FieldArray>

                        {/* Hydration */}
                        <h6 className="fw-bold mt-4">Hydration Given</h6>
                        <h6 className="mt-2">Pre–Chemo Hydration</h6>
                        <div className="row">
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Fluid Type"
                                    fullWidth
                                    name="preChemoHydrationFluidType"
                                    value={values?.preChemoHydrationFluidType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Volume"
                                    fullWidth
                                    name="preChemoHydrationFluidVolume"
                                    value={values?.preChemoHydrationFluidVolume}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Duration"
                                    fullWidth
                                    name="preChemoHydrationFluidDuration"
                                    value={values?.preChemoHydrationFluidDuration}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>

                        <h6 className="mt-2">Post–Chemo Hydration</h6>
                        <div className="row">
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Fluid Type"
                                    fullWidth
                                    name="postChemoHydrationFluidType"
                                    value={values?.postChemoHydrationFluidType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Volume"
                                    fullWidth
                                    name="postChemoHydrationFluidVolume"
                                    value={values?.postChemoHydrationFluidVolume}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-md-4 col-12 mb-3">
                                <TextField
                                    label="Duration"
                                    fullWidth
                                    name="postChemoHydrationFluidDuration"
                                    value={values?.postChemoHydrationFluidDuration}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>

                        {/* Other Fields */}
                        <TextField
                            label="Any Significant Side Effects Reported"
                            fullWidth
                            className="mb-3"
                            name="significantSideEffects"
                            value={values?.significantSideEffects}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <TextField
                            label="Medicines Prescribed"
                            fullWidth
                            className="mb-3"
                            name="medicinePrescribed"
                            value={values?.medicinePrescribed}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <TextField
                            label="Discharge Summary"
                            fullWidth
                            className="mb-3"
                            name="dischargeSummary"
                            value={values?.dischargeSummary}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <TextField
                            label="Name of Doctor"
                            fullWidth
                            className="mb-3"
                            name="doctorName"
                            value={values?.doctorName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(errors?.doctorName && touched?.doctorName)}
                            helperText={touched?.doctorName && errors?.doctorName}
                            required
                        />
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default ChemoAdministration;